/** How often the page on screen is checked, and how much on-screen time is sent at once. */
export const TICK_MS = 5_000;
const SEND_EVERY_SECONDS = 30;

const ID = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[a-z0-9]{20,})$/i;

/** The page as Demo insights groups it: ids become `:id`, and only the `tab` of the query is kept. */
export function pageOf(location: Pick<Location, 'pathname' | 'search'>): string {
  const path = location.pathname.split('/').map((part) => (ID.test(part) ? ':id' : part)).join('/') || '/';
  const tab = new URLSearchParams(location.search).get('tab');
  return tab ? `${path}?tab=${tab}` : path;
}

const newVisit = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);
type Send = (beat: { page: string; visitId: string; seconds: number }) => Promise<unknown>;

/**
 * Counts how long each page of a private copy is on screen (only while the tab is visible) and sends it in beats: every 30
 * seconds, and whenever the page changes or the tab is hidden. A page shown again after the tab was hidden is a new visit.
 * Returns the stop.
 */
export function trackViews(send: Send, where: () => Pick<Location, 'pathname' | 'search'> = () => window.location): () => void {
  let page = pageOf(where());
  let visitId = newVisit();
  let unsent = 0;
  const flush = () => {
    if (unsent <= 0) return;
    void send({ page, visitId, seconds: unsent }).catch(() => undefined);
    unsent = 0;
  };
  const tick = () => {
    const now = pageOf(where());
    if (now !== page) { flush(); page = now; visitId = newVisit(); }
    if (document.visibilityState !== 'visible') return;
    unsent += TICK_MS / 1_000;
    if (unsent >= SEND_EVERY_SECONDS) flush();
  };
  const onHidden = () => {
    if (document.visibilityState !== 'visible') { flush(); return; }
    visitId = newVisit();
    void send({ page, visitId, seconds: 0 }).catch(() => undefined);
  };
  const timer = setInterval(tick, TICK_MS);
  document.addEventListener('visibilitychange', onHidden);
  // The first beat says the page was opened, before any time is counted.
  void send({ page, visitId, seconds: 0 }).catch(() => undefined);
  return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onHidden); flush(); };
}
